import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import type { PurchaseOrderRegisterRow, PurchaseRequestRegisterRow, SupplierPerformanceRow } from '@/types/reports';

const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function ProcurementSlide() {
  const { t } = useTranslation('dashboard');
  const colors = useCategoryColors();

  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['reports', 'purchase-order-register'],
    queryFn: async () => (await apiClient.get<PurchaseOrderRegisterRow[]>('/reports/purchase-order-register')).data,
    refetchInterval: 60_000,
  });
  const { data: purchaseRequests = [] } = useQuery({
    queryKey: ['reports', 'purchase-request-register'],
    queryFn: async () => (await apiClient.get<PurchaseRequestRegisterRow[]>('/reports/purchase-request-register')).data,
    refetchInterval: 60_000,
  });
  const { data: supplierPerformance = [] } = useQuery({
    queryKey: ['reports', 'supplier-performance'],
    queryFn: async () => (await apiClient.get<SupplierPerformanceRow[]>('/reports/supplier-performance')).data,
    refetchInterval: 60_000,
  });

  const totalPoCount = useMemo(() => new Set(purchaseOrders.map((r) => r.poId)).size, [purchaseOrders]);
  const totalPoValue = useMemo(() => purchaseOrders.reduce((sum, r) => sum + r.orderedQty * r.unitPrice, 0), [purchaseOrders]);
  const pendingPrCount = useMemo(() => new Set(purchaseRequests.filter((r) => !r.isClosed).map((r) => r.prId)).size, [purchaseRequests]);
  const avgOnTime = useMemo(() => {
    const values = supplierPerformance.map((s) => s.onTimeDeliveryPercent).filter((v): v is number => v != null);
    return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
  }, [supplierPerformance]);

  const monthlyTrend = useMemo(
    () => groupSumByMonth(purchaseOrders, (r) => r.orderDate, [{ key: 'value', value: (r) => r.orderedQty * r.unitPrice }]),
    [purchaseOrders],
  );
  const topSuppliers = useMemo(() => topN(supplierPerformance, (r) => r.totalOrderedQty, 5), [supplierPerformance]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('procurement.totalPos')} value={totalPoCount} icon={<AssignmentIcon />} accentColor={colors.procurement} />
        <TvKpiTile label={t('procurement.totalPoValue')} value={formatCurrency(totalPoValue)} icon={<PaymentsIcon />} accentColor={colors.procurement} />
        <TvKpiTile label={t('procurement.pendingPrs')} value={pendingPrCount} icon={<PendingActionsIcon />} accentColor={colors.procurement} />
        <TvKpiTile label={t('procurement.avgOnTime')} value={avgOnTime != null ? `${avgOnTime.toFixed(0)}%` : '—'} icon={<LocalShippingIcon />} accentColor={colors.procurement} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('procurement.trendTitle')} subtitle={t('procurement.trendSubtitle')} height={420}>
          <DashboardLineChart
            categories={monthlyTrend.map((m) => m.month)}
            series={[{ label: t('procurement.totalPoValue'), data: monthlyTrend.map((m) => m.values.value), color: colors.procurement, area: true }]}
            valueFormatter={(v) => formatCurrency(v ?? 0)}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList
          title={t('procurement.topSuppliersTitle')}
          accentColor={colors.procurement}
          items={topSuppliers.map((s) => ({ label: s.supplierName, value: s.totalOrderedQty }))}
        />
      </Box>
    </Box>
  );
}
